import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users,
  Building2,
  Activity,
  AlertCircle,
  ExternalLink,
  Eye,
  X,
  Calendar,
  MapPin,
  DollarSign,
} from 'lucide-react';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import PillSelect from '../../../components/UI/PillSelect';
import { Pagination } from '../../../components/UI/Pagination';
import Portal from '../../../components/UI/Portal';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import { InfoBadge } from '../../../components/UI/InfoBadge';
import { TradeBadge } from '../../../components/UI/TradeBadge';
import { isTradeRedundantWithDepartment } from '../../../config/tradeConfig';
import {
  formatMoney,
  formatDate,
  formatMonthLabel,
  SourceOfTruthBanner,
  PortalUnavailableBanner,
} from './shared';

// Resident-row workflow states translated to canonical StatusBadge keys.
// Centralised here so the table cell, the modal, and any future surfaces
// stay in lock-step with the DS status taxonomy (Active / Pending / Inactive).
const RESIDENT_ROW_STATUS = {
  unmatched: { status: 'pending', label: 'Unmatched in HR' },
  assigned:  { status: 'active',  label: 'Assigned' },
  left:      { status: 'inactive', label: 'Left' },
};

// HR `employment_status` (`active`/`probation`/`notice`/`inactive`) → DS
// StatusBadge key + display label. Matches the convention already used
// on the Profile page so the same worker reads identically across modules.
const EMPLOYMENT_STATUS_PRESENTATION = {
  active:    { status: 'active',    label: 'Active' },
  probation: { status: 'pending',   label: 'Probation' },
  notice:    { status: 'pending',   label: 'Notice' },
  inactive:  { status: 'inactive',  label: 'Inactive' },
  terminated:{ status: 'terminated', label: 'Terminated' },
};

const renderEmploymentStatusBadge = (raw) => {
  if (!raw) {
    return <span className="text-sm text-gray-400 italic">Not in HR</span>;
  }
  const key = String(raw).toLowerCase();
  const cfg = EMPLOYMENT_STATUS_PRESENTATION[key];
  if (cfg) return <StatusBadge status={cfg.status} label={cfg.label} />;
  // Unknown server value — surface as neutral with title-cased label, never
  // render the raw schema string verbatim.
  const fallbackLabel = key.charAt(0).toUpperCase() + key.slice(1);
  return <StatusBadge status="inactive" label={fallbackLabel} />;
};

const PAGE_SIZE = 25;

/**
 * Assignments tab — read-only table of resident → dormitory mappings,
 * sourced from the Dormitory Portal.
 *
 * The page answers two questions HR opens this tab to ask:
 *   1. "Where does worker X live?" — search by name / staff_id.
 *   2. "Who lived in dormitory Y this month?" — filter / scan.
 *
 * Stats banner at the top surfaces the operational signals: how many
 * residents are currently assigned, how many left this month, how many
 * appear in the Dormitory Portal but no longer in HR (data integrity), and
 * how many HR Worker-class staff have no dorm record at all.
 */
const AssignmentsTab = ({ portalResidents, loading, onRefresh }) => {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedResident, setSelectedResident] = useState(null);

  // Hooks must run unconditionally on every render — pull data with safe
  // defaults BEFORE useMemo so the first-load skeleton path doesn't skip it.
  const residents = portalResidents?.residents || [];
  const stats = portalResidents?.stats || null;
  const monthLabel = portalResidents?.month_label;
  const monthDisplay = formatMonthLabel(monthLabel);
  const dormPortalUrl = portalResidents?.dorm_portal_url;
  const isAvailable = portalResidents?.available !== false;
  const portalError = portalResidents?.error;

  const filteredResidents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return residents.filter((r) => {
      if (q) {
        const hay = [
          r.full_name,
          r.staff_id,
          r.department,
          r.trade_code,
          r.current_segment?.dorm_name,
          r.current_segment?.location_label,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const segment = r.current_segment || {};
      if (statusFilter === 'currently_assigned' && !segment.is_open) return false;
      if (statusFilter === 'left_this_month' && segment.is_open) return false;
      if (statusFilter === 'unmatched_in_hr' && r.is_employed_in_hr !== false) return false;
      return true;
    });
  }, [residents, searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredResidents.length / PAGE_SIZE);
  const paginatedResidents = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredResidents.slice(start, start + PAGE_SIZE);
  }, [filteredResidents, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (loading && !portalResidents) {
    return <AssignmentsSkeleton />;
  }

  return (
    <div className="space-y-6">
      <SourceOfTruthBanner
        monthLabel={monthLabel}
        dormPortalUrl={dormPortalUrl}
        onRefresh={onRefresh}
        loading={loading}
      />

      {!isAvailable && <PortalUnavailableBanner error={portalError} />}

      {stats && <AssignmentStatsStrip stats={stats} dormPortalUrl={dormPortalUrl} />}

      {/* Toolbar — same DS pattern as Locations: search + PillSelect */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search by name, staff ID, department, or dormitory..."
            theme="admin"
            className="max-w-xl"
          />

          <div className="flex items-center gap-2">
            <PillSelect
              options={[
                { value: 'all', label: 'All residents' },
                { value: 'currently_assigned', label: 'Currently assigned' },
                { value: 'left_this_month', label: 'Left this month' },
                { value: 'unmatched_in_hr', label: 'Unmatched in HR' },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              icon={Activity}
              theme="admin"
            />
          </div>
        </div>
      </div>

      {/* Result count strip */}
      {residents.length > 0 && (
        <div className="text-xs text-gray-500 px-1">
          Showing {filteredResidents.length} of {residents.length} resident
          {residents.length === 1 ? '' : 's'}
          {monthDisplay && ` · ${monthDisplay}`}
        </div>
      )}

      {/* Residents — mobile cards + desktop table (DS: wide tables MUST render
          stacked cards below md; an 8-column table can't fit a phone). */}
      {filteredResidents.length === 0 ? (
        <EmptyResidents
          hasFilters={Boolean(searchTerm || statusFilter !== 'all')}
          isAvailable={isAvailable}
        />
      ) : (
        <>
          {/* Mobile: one card per resident */}
          <div className="md:hidden space-y-3">
            {paginatedResidents.map((r) => (
              <ResidentCard
                key={r.resident_id || r.staff_id}
                resident={r}
                onView={setSelectedResident}
              />
            ))}
          </div>

          {/* Desktop: full table inside the bordered panel */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">
            <ResidentsTable
              residents={paginatedResidents}
              onViewResident={setSelectedResident}
            />
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalCount={filteredResidents.length}
            pageSize={PAGE_SIZE}
            theme="admin"
            noun="residents"
          />
        </>
      )}

      {selectedResident && (
        <ResidentDetailModal
          resident={selectedResident}
          monthDisplay={monthDisplay}
          dormPortalUrl={dormPortalUrl}
          onClose={() => setSelectedResident(null)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stats strip — operational signals about the resident roster.
// Each metric carries a *semantic* icon colour per the DS "Color Coding"
// rule (blue/green/orange/red for different metrics) so the eye instantly
// distinguishes "healthy state" cards from "needs reconciliation" cards.
// The two reconciliation cards additionally tint amber when their count is
// non-zero — the colour is the actionability signal, not just decoration.
// ---------------------------------------------------------------------------
const AssignmentStatsStrip = ({ stats, dormPortalUrl }) => {
  const unassignedWarn = (stats.unassigned_hr_workers ?? 0) > 0;
  const unmatchedWarn = (stats.unmatched_in_hr ?? 0) > 0;

  const cards = [
    {
      label: 'Currently assigned',
      value: stats.currently_assigned ?? 0,
      icon: Users,
      // Green ⇒ healthy / fulfilled — the desired steady state.
      iconBg: 'bg-green-100',
      iconColor: 'text-green-700',
      surface: 'bg-white',
      valueColor: 'text-gray-900',
      labelColor: 'text-gray-600',
    },
    {
      label: 'Left this month',
      value: stats.leaving_this_month ?? 0,
      icon: Activity,
      // Blue ⇒ informational / movement signal — neither good nor bad.
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-700',
      surface: 'bg-white',
      valueColor: 'text-gray-900',
      labelColor: 'text-gray-600',
    },
    {
      label: 'Unassigned HR workers',
      value: stats.unassigned_hr_workers ?? 0,
      icon: AlertCircle,
      // Amber when non-zero (actionable warning), neutral grey when clean.
      iconBg: unassignedWarn ? 'bg-amber-100' : 'bg-gray-100',
      iconColor: unassignedWarn ? 'text-amber-700' : 'text-gray-600',
      surface: unassignedWarn ? 'bg-amber-50 border-amber-200' : 'bg-white',
      valueColor: unassignedWarn ? 'text-amber-900' : 'text-gray-900',
      labelColor: unassignedWarn ? 'text-amber-800' : 'text-gray-600',
      hint: 'Active workers in HR with no dorm record',
    },
    {
      label: 'Unmatched in HR',
      value: stats.unmatched_in_hr ?? 0,
      icon: AlertCircle,
      // Red when non-zero (the more severe data-integrity miss), grey when clean.
      iconBg: unmatchedWarn ? 'bg-red-100' : 'bg-gray-100',
      iconColor: unmatchedWarn ? 'text-red-600' : 'text-gray-600',
      surface: unmatchedWarn ? 'bg-red-50 border-red-200' : 'bg-white',
      valueColor: unmatchedWarn ? 'text-red-900' : 'text-gray-900',
      labelColor: unmatchedWarn ? 'text-red-800' : 'text-gray-600',
      hint: 'Residents in portal but not in HR',
    },
  ];

  return (
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.label}
            className={`rounded-lg border p-4 ${card.surface}`}
            title={card.hint || undefined}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${card.iconBg}`}
              >
                <Icon className={`w-4 h-4 ${card.iconColor}`} />
              </div>
              <div className="min-w-0">
                <p className={`text-xl font-bold tabular-nums ${card.valueColor}`}>
                  {Number(card.value).toLocaleString()}
                </p>
                <p className={`text-xs ${card.labelColor} truncate`}>
                  {card.label}
                </p>
              </div>
            </div>
          </div>
        );
      })}
      {(unassignedWarn || unmatchedWarn) && dormPortalUrl && (
        <div className="col-span-2 lg:col-span-4">
          <p className="text-xs text-gray-500">
            Reconcile mismatches in the{' '}
            <a
              href={dormPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              Dormitory Portal
              <ExternalLink className="w-3 h-3" />
            </a>
            .
          </p>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Residents table — DS classes only. Status pill conveys the row state
// (currently assigned vs left this month vs unmatched-in-HR).
// ---------------------------------------------------------------------------
const ResidentsTable = ({ residents, onViewResident }) => (
  // Horizontal scroll wrapper — 8 columns can't fit a phone viewport. The
  // parent card keeps `overflow-hidden` for rounded corners, so without this
  // inner scroller the right-hand columns (Status / Cost / Details) get
  // clipped with no way to reach them. Scroll the table in isolation.
  <div className="overflow-x-auto">
  <table className="min-w-full divide-y divide-gray-200">
    <thead className="table-header">
      <tr>
        <th scope="col" className="table-header-cell">Worker</th>
        <th scope="col" className="table-header-cell">Department</th>
        <th scope="col" className="table-header-cell">Dormitory</th>
        <th scope="col" className="table-header-cell">Room / Bed</th>
        <th scope="col" className="table-header-cell">Move-in</th>
        <th scope="col" className="table-header-cell">Status</th>
        <th scope="col" className="table-header-cell text-right">Cost (this month)</th>
        <th scope="col" className="table-header-cell text-right">Details</th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-100">
      {residents.map((r) => {
        const segment = r.current_segment || {};
        const isOpen = segment.is_open;
        const isUnmatched = r.is_employed_in_hr === false;
        const bedDisplay = getBedDisplay(segment.room_number, segment.bed_label);

        return (
          <tr key={r.resident_id || r.staff_id} className="table-row">
            <td className="table-cell px-6 py-4">
              <div className="text-sm font-medium text-gray-900 truncate">
                {r.full_name || '—'}
              </div>
              <div className="text-xs text-gray-500">
                {r.staff_id || '—'}
              </div>
            </td>
            <td className="table-cell px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-700 truncate max-w-xs">
                {r.department || (
                  <span className="text-gray-400 italic">Not in HR</span>
                )}
              </div>
              {r.trade_code &&
                !isTradeRedundantWithDepartment(r.department, r.trade_code) && (
                <div className="mt-1">
                  <TradeBadge code={r.trade_code} size="xs" />
                </div>
              )}
            </td>
            <td className="table-cell px-6 py-4 whitespace-nowrap">
              {/* Dormitory cell — name only. Ownership ("Company" /
                  "Third-party") lives on the Locations tab where it is
                  the primary identifier; here it is noise. */}
              <div className="flex items-center gap-2 text-sm text-gray-900">
                <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="truncate max-w-xs">
                  {segment.dorm_name || '—'}
                </span>
              </div>
            </td>
            <td className="table-cell px-6 py-4 whitespace-nowrap text-xs text-gray-500">
              {segment.room_number || bedDisplay ? (
                <>
                  {segment.room_number && `Room ${segment.room_number}`}
                  {segment.room_number && bedDisplay && ' · '}
                  {bedDisplay && `Bed ${bedDisplay}`}
                </>
              ) : (
                '—'
              )}
              {r.has_multiple_segments_in_month && (
                <div className="text-[10px] text-blue-600 mt-0.5">
                  {r.segment_count} stays this month
                </div>
              )}
            </td>
            <td className="table-cell px-6 py-4 whitespace-nowrap text-xs text-gray-500 tabular-nums">
              {formatDate(segment.move_in_date)}
              {segment.move_out_date && (
                <div className="text-[10px] mt-0.5">
                  out: {formatDate(segment.move_out_date)}
                </div>
              )}
            </td>
            <td className="table-cell px-6 py-4 whitespace-nowrap">
              <ResidentRowStatusBadge
                isOpen={isOpen}
                isUnmatched={isUnmatched}
              />
            </td>
            <td className="table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold tabular-nums">
              {formatMoney(r.total_prorated_cost)}
            </td>
            <td className="table-cell px-6 py-4 whitespace-nowrap text-right">
              <button
                type="button"
                onClick={() => onViewResident(r)}
                className="icon-button-admin"
                title={`View ${r.full_name || r.staff_id || 'resident'} details`}
                aria-label={`View ${r.full_name || r.staff_id || 'resident'} details`}
              >
                <Eye className="w-4 h-4" />
              </button>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
  </div>
);

// ---------------------------------------------------------------------------
// Resident card — mobile twin of one table row. Leads with the field HR
// scans for (worker name), anchors the row status top-right, lists the
// key assignment facts as label/value rows, and puts the view-details
// action bottom-right (DS card-action placement standard).
// ---------------------------------------------------------------------------
const ResidentCard = ({ resident: r, onView }) => {
  const segment = r.current_segment || {};
  const isOpen = segment.is_open;
  const isUnmatched = r.is_employed_in_hr === false;
  const bedDisplay = getBedDisplay(segment.room_number, segment.bed_label);
  const roomBed = segment.room_number || bedDisplay
    ? [
        segment.room_number && `Room ${segment.room_number}`,
        bedDisplay && `Bed ${bedDisplay}`,
      ].filter(Boolean).join(' · ')
    : '—';

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{r.full_name || '—'}</p>
          <p className="text-xs text-gray-500 truncate flex items-center gap-1.5 flex-wrap">
            <span>{r.staff_id || '—'}</span>
            {r.trade_code &&
              !isTradeRedundantWithDepartment(r.department, r.trade_code) && (
              <TradeBadge code={r.trade_code} size="xs" />
            )}
          </p>
        </div>
        <div className="flex-shrink-0">
          <ResidentRowStatusBadge isOpen={isOpen} isUnmatched={isUnmatched} />
        </div>
      </div>

      <div className="mt-3 space-y-1.5 text-sm flex-1">
        <div className="flex items-start justify-between gap-3">
          <span className="text-gray-500 flex-shrink-0">Department</span>
          <span className="text-gray-900 text-right truncate">
            {r.department || <span className="text-gray-400 italic">Not in HR</span>}
          </span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <span className="text-gray-500 flex-shrink-0">Dormitory</span>
          <span className="text-gray-900 text-right truncate min-w-0">{segment.dorm_name || '—'}</span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <span className="text-gray-500 flex-shrink-0">Room / Bed</span>
          <span className="text-gray-900 text-right">{roomBed}</span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <span className="text-gray-500 flex-shrink-0">Move-in</span>
          <span className="text-gray-900 text-right tabular-nums">
            {formatDate(segment.move_in_date)}
            {segment.move_out_date ? ` → ${formatDate(segment.move_out_date)}` : ''}
          </span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <span className="text-gray-500 flex-shrink-0">Cost (this month)</span>
          <span className="text-gray-900 font-semibold text-right tabular-nums">
            {formatMoney(r.total_prorated_cost)}
          </span>
        </div>
      </div>

      <div className="card-action-footer">
        <button
          type="button"
          onClick={() => onView(r)}
          className="icon-button-admin"
          title={`View ${r.full_name || r.staff_id || 'resident'} details`}
          aria-label={`View ${r.full_name || r.staff_id || 'resident'} details`}
        >
          <Eye className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const getBedDisplay = (roomNumber, bedLabel) => {
  if (!bedLabel) return '';
  const room = String(roomNumber || '').trim();
  const bed = String(bedLabel).trim();
  if (room && bed.startsWith(`${room}-`)) {
    return bed.slice(room.length + 1);
  }
  return bed;
};

const getRoomBedLabel = (segment) => {
  const bedDisplay = getBedDisplay(segment?.room_number, segment?.bed_label);
  if (segment?.room_number && bedDisplay) {
    return `Room ${segment.room_number} · Bed ${bedDisplay}`;
  }
  if (segment?.room_number) return `Room ${segment.room_number}`;
  if (bedDisplay) return `Bed ${bedDisplay}`;
  return '—';
};

// ---------------------------------------------------------------------------
// Resident detail modal — strict DS modal pattern:
//   Portal → modal-overlay → modal-container modal-container-lg
//          → modal-header → modal-content → modal-footer
//
// Inside the modal, every status indicator goes through the canonical
// `StatusBadge` (workflow states) or `InfoBadge` (identity / category),
// never raw inline Tailwind. Schema strings (`active`, `probation`, …) are
// translated to title-cased display labels — the modal never surfaces
// schema English to HR users.
// ---------------------------------------------------------------------------
const ResidentDetailModal = ({ resident, monthDisplay, dormPortalUrl, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const current = resident.current_segment || {};
  const segments = resident.segments || [];
  const isUnmatched = resident.is_employed_in_hr === false;
  const periodPhrase = monthDisplay || 'this month';

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="modal-container modal-container-lg"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="modal-header">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="modal-title truncate">
                  {resident.full_name || 'Resident details'}
                </h3>
                {/* Header-level status — DS allows StatusBadge in modal
                    titles for prominent workflow context. */}
                <ResidentRowStatusBadge
                  isOpen={current.is_open}
                  isUnmatched={isUnmatched}
                />
              </div>
              <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                <span>{resident.staff_id || 'No staff ID'}</span>
                {resident.trade_code &&
                  !isTradeRedundantWithDepartment(resident.department, resident.trade_code) && (
                  <TradeBadge code={resident.trade_code} size="xs" />
                )}
              </p>
            </div>
            <button type="button" onClick={onClose} className="modal-close-admin">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-content space-y-6">
            {/* HR identity + current assignment summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailPanel title="HR record">
                <DetailRow
                  label="Department"
                  value={resident.department || (
                    <span className="text-gray-400 italic">Not in HR</span>
                  )}
                />
                <DetailRow
                  label="Trade"
                  value={resident.trade_code || '—'}
                />
                <DetailRow
                  label="Employment status"
                  value={renderEmploymentStatusBadge(resident.employment_status)}
                />
              </DetailPanel>

              <DetailPanel title="Current dormitory">
                <DetailRow label="Dormitory" value={current.dorm_name || '—'} />
                <DetailRow label="Room / Bed" value={getRoomBedLabel(current)} />
                <DetailRow label="Monthly rate" value={formatMoney(current.rate_monthly)} />
              </DetailPanel>
            </div>

            {/* Movement history */}
            <section>
              <div className="flex items-center justify-between gap-2 sm:gap-4 mb-3">
                <div className="min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 truncate">Movement history</h4>
                  <p className="text-xs text-gray-500 truncate">
                    Stay segments recorded by the Dormitory Portal for {periodPhrase}.
                  </p>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 flex-shrink-0 whitespace-nowrap">
                  {segments.length} stay{segments.length === 1 ? '' : 's'}
                </span>
              </div>

              {segments.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No movement records
                  </h3>
                  <p className="text-sm text-gray-500">
                    No stay segments were returned by the Dormitory Portal for this worker.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {segments.map((segment, index) => (
                    <MovementCard
                      key={segment.period_id || `${segment.move_in_date}-${index}`}
                      segment={segment}
                      isLast={index === segments.length - 1}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-admin-outline">
              Close
            </button>
            {dormPortalUrl && (
              <a
                href={dormPortalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-admin-primary"
              >
                <ExternalLink className="w-4 h-4" />
                Open Dormitory Portal
              </a>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
};

const DetailPanel = ({ title, children }) => (
  <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
    <h4 className="text-sm font-semibold text-gray-900 mb-3">{title}</h4>
    <div className="space-y-2">{children}</div>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-900 font-medium text-right">{value}</span>
  </div>
);

const MovementCard = ({ segment, isLast }) => (
  <div className="bg-white border rounded-xl p-4">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-900 flex-wrap">
          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="truncate">{segment.dorm_name || 'Unknown dormitory'}</span>
          {/* Segment marker — InfoBadge for the "this is the live one" /
              "this is the most recent closed one" identity, since these
              are *segment positions* not workflow states. */}
          {segment.is_open && (
            <InfoBadge color="green" label="Current" />
          )}
          {!segment.is_open && isLast && (
            <InfoBadge color="gray" label="Latest" />
          )}
        </div>
        {segment.address && (
          <p className="flex items-center gap-1 text-xs text-gray-500 mt-1">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{segment.address}</span>
          </p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-semibold text-gray-900">
          {formatMoney(segment.prorated_cost)}
        </p>
        <p className="text-xs text-gray-500">
          {segment.month_overlap_days || 0} day{segment.month_overlap_days === 1 ? '' : 's'}
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-3 border-t border-gray-100">
      <MovementMeta icon={Calendar} label="Period" value={`${formatDate(segment.move_in_date)} → ${segment.move_out_date ? formatDate(segment.move_out_date) : 'Present'}`} />
      <MovementMeta icon={Building2} label="Room / Bed" value={getRoomBedLabel(segment)} />
      <MovementMeta icon={DollarSign} label="Monthly rate" value={formatMoney(segment.rate_monthly)} />
    </div>
  </div>
);

const MovementMeta = ({ icon: Icon, label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">
      {label}
    </p>
    <p className="flex items-center gap-1.5 text-xs text-gray-700">
      <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      <span>{value}</span>
    </p>
  </div>
);

// Resident-row status — derived from the segment open/close flag and the
// HR-employment match flag, then rendered through the canonical DS
// StatusBadge so colour, sizing, and ring all stay aligned with every
// other workflow status in the portal.
const ResidentRowStatusBadge = ({ isOpen, isUnmatched }) => {
  let cfg;
  if (isUnmatched) cfg = RESIDENT_ROW_STATUS.unmatched;
  else if (isOpen) cfg = RESIDENT_ROW_STATUS.assigned;
  else cfg = RESIDENT_ROW_STATUS.left;
  return <StatusBadge status={cfg.status} label={cfg.label} />;
};

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------
const EmptyResidents = ({ hasFilters, isAvailable }) => (
  <div className="bg-white border rounded-xl py-12 text-center">
    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-semibold text-gray-900 mb-2">
      {hasFilters ? 'No residents match your filters' : 'No residents'}
    </h3>
    <p className="text-sm text-gray-500 max-w-sm mx-auto">
      {hasFilters
        ? 'Try clearing the search or status filter.'
        : isAvailable
          ? 'No residents recorded in the Dormitory Portal for this month.'
          : 'Could not load residents from the Dormitory Portal.'}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------
const AssignmentsSkeleton = () => (
  <div className="space-y-6">
    <div className="h-16 bg-blue-50 border border-blue-200 rounded-lg animate-pulse" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white border rounded-lg p-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-5 bg-gray-200 rounded w-12" />
              <div className="h-3 bg-gray-200 rounded w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="bg-white shadow-sm rounded-lg p-6 animate-pulse">
      <div className="flex items-center justify-between gap-3">
        <div className="h-11 bg-gray-200 rounded w-80" />
        <div className="h-11 bg-gray-200 rounded w-44" />
      </div>
    </div>
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden animate-pulse">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="h-3 bg-gray-200 rounded flex-1" />
          ))}
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="px-6 py-4">
            <div className="flex items-center gap-4">
              {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                <div key={j} className="h-4 bg-gray-100 rounded flex-1" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default AssignmentsTab;
